import { useEffect, useContext, useState } from "react";
import { Button, Label, Select, Textarea, TextInput } from "flowbite-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useMutationData } from "../../../hooks/useMutationData";
import { useGetData } from "../../../hooks/useGetData";
import { EvaluadoInterface } from "../../../interfaces/EvaluadoInterface";
import { EmpresaInterface } from "../../../interfaces/EmpresaInterface";
import { Loading } from "../../../components/Loading/Loading";
import { ThemeContext } from "../../../components/ThemeContext/ThemeContext";
import CameraInput from "../../../components/CameraInput/CameraInput";
import { sendPhotoToBackend } from "../../../components/CameraInput/CameraInput";
//Constantes
import { API } from "../../../global/global";

export const EvaluadosEditarScreen = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { control, handleSubmit, reset } = useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode } = useContext(ThemeContext);
  const [numeroHijos, setNumeroHijos] = useState(0);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);

  const [tipoEvaluado, setTipoEvaluado] = useState<string>("todos");

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tipoParam = searchParams.get("tipo");

    if (tipoParam) {
      setTipoEvaluado(tipoParam);
      localStorage.setItem("ultimoTipoEvaluado", tipoParam);
    } else {
      const tipoGuardado = localStorage.getItem("ultimoTipoEvaluado");
      if (tipoGuardado) {
        setTipoEvaluado(tipoGuardado);
      }
    }
  }, [location.search]);

  // Función para formatear texto (primera mayúscula, resto minúsculas)
  const formatTitleCase = (value: string): string => {
    if (!value) return value;
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  };

  // Función para formatear a minúsculas
  const formatLowerCase = (value: string): string => {
    if (!value) return value;
    return value.toLowerCase();
  };

  // Función para formatear a mayúsculas
  const formatUpperCase = (value: string): string => {
    if (!value) return value;
    return value.toUpperCase();
  };

  // Función para manejar campos numéricos (solo positivos)
  const handleNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: { onChange: (value: number) => void }
  ): void => {
    const value = Math.max(0, parseInt(e.target.value) || 0);
    field.onChange(value);
  };

  const mutateEvaluado = useMutationData({
    url: `/evaluado/${uuid}`,
    reload: ["evaluado", `${uuid}`],
  });

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${uuid}`,
    name: ["evaluado"],
  });

  const queryEmpresas = useGetData<EmpresaInterface[]>({
    url: `/empresa?estatus[eq]=1`,
    name: ["empresa"],
  });

  useEffect(() => {
    if (queryEvaluado.data) {
      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        correo: queryEvaluado.data.correo || "",
        telefono: queryEvaluado.data.telefono || "",
        empresa_id: String(queryEvaluado.data.empresa_id.id),
        puesto: queryEvaluado.data.puesto || "",
        fecha_ingreso: queryEvaluado.data.fecha_ingreso || "",
        antiguedad: queryEvaluado.data.antiguedad || "",
        fecha_nacimiento: queryEvaluado.data.fecha_nacimiento || "",
        genero: queryEvaluado.data.genero || "",
        lugar_nacimiento: queryEvaluado.data.lugar_nacimiento || "",
        nacionalidad: queryEvaluado.data.nacionalidad || "",
        curp: queryEvaluado.data.curp || "",
        rfc: queryEvaluado.data.rfc || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        calle: queryEvaluado.data.calle || "",
        numero_exterior: queryEvaluado.data.numero_exterior || "",
        numero_interior: queryEvaluado.data.numero_interior || "",
        referencia: queryEvaluado.data.referencia || "",
        colonia: queryEvaluado.data.colonia || "",
        codigo_postal: queryEvaluado.data.codigo_postal || "",
        municipio: queryEvaluado.data.municipio || "",
        estado: queryEvaluado.data.estado || "",
        pais: queryEvaluado.data.pais || "",
        anios_domicilio_viviendo:
          queryEvaluado.data.anios_domicilio_viviendo || "",
        personas_vivienda: queryEvaluado.data.personas_vivienda || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        vive_con: queryEvaluado.data.vive_con || "",
        tiempo_casado: queryEvaluado.data.tiempo_casado || "",
        nombre_conyuge: queryEvaluado.data.nombre_conyuge || "",
        conyuge_oficio: queryEvaluado.data.conyuge_oficio || "",
        cuantos_hijos: queryEvaluado.data.cuantos_hijos || "",
        nombre_hijo1: queryEvaluado.data.nombre_hijo1 || "",
        edad_hijo1: queryEvaluado.data.edad_hijo1 || "",
        nombre_hijo2: queryEvaluado.data.nombre_hijo2 || "",
        edad_hijo2: queryEvaluado.data.edad_hijo2 || "",
        nombre_hijo3: queryEvaluado.data.nombre_hijo3 || "",
        edad_hijo3: queryEvaluado.data.edad_hijo3 || "",
        nombre_hijo4: queryEvaluado.data.nombre_hijo4 || "",
        edad_hijo4: queryEvaluado.data.edad_hijo4 || "",
        telefono_recados: queryEvaluado.data.telefono_recados || "",
        empresa_labora: queryEvaluado.data.empresa_labora || "",
        fecha_evaluacione: queryEvaluado.data.fecha_evaluacione || "",
        sepostula_evalua: queryEvaluado.data.sepostula_evalua || "",
        motivo_evaluacion: queryEvaluado.data.motivo_evaluacion || "",
        tipo_evaluacion: queryEvaluado.data.tipo_evaluacion || "",
      });

      setNumeroHijos(Number(queryEvaluado.data.cuantos_hijos) || 0);

      const normalizeString = (str: string) => {
        // Normaliza la cadena y elimina los acentos
        return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      };

      const nombreCompleto = normalizeString(
        `${queryEvaluado.data?.nombre}_${queryEvaluado.data?.apellido_paterno}_${queryEvaluado.data?.apellido_materno}`.replace(
          /\s/g,
          "_"
        )
      );

      const token = localStorage.getItem("token");
      fetch(`${API}/evaluados/${uuid}/photo/${nombreCompleto}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((data) => {
          console.log("Respuesta del servidor:", data);

          if (data.url) {
            // No se le agrega timestamp: la URL ya es una URL firmada y
            // temporal (expires+signature); añadirle otro parámetro rompe
            // la firma y el backend la rechaza.
            setPhotoUrl(data.url);
            console.log("URL de la foto:", data.url);
          } else {
            setPhotoUrl(null);
          }
        })
        .catch((error) => {
          console.error("Error al obtener la URL de la foto:", error);
          setPhotoUrl(null);
        });
    }
  }, [queryEvaluado.data, reset, uuid]);

  const handleCancel = () => {
    navigate(`/evaluadosList?tipo=${tipoEvaluado}`);
  };

  if (
    queryEvaluado.isLoading ||
    mutateEvaluado.isPending ||
    queryEmpresas.isLoading
  ) {
    return <Loading />;
  }

  //const normalizeString = (str: string) => {
  //  // Normaliza la cadena y elimina los acentos
  //  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  //};
  //
  //const nombreCompleto = normalizeString(
  //  `${queryEvaluado.data?.nombre}_${queryEvaluado.data?.apellido_paterno}_${queryEvaluado.data?.apellido_materno}`.replace(
  //    /\s/g,
  //    "_"
  //  )
  //);

  return (
    <div
      className={`min-h-screen p-6 ${
        isDarkMode ? "bg-gray-800 text-white" : "bg-white text-black"
      }`}
    >
      <div
        className={`max-w-4xl mx-auto p-6 rounded-lg shadow-lg space-y-6 ${
          isDarkMode ? "bg-gray-700" : "bg-white"
        }`}
      >
        <div
          className={`bg-gradient-to-r p-6 text-white rounded-t-lg ${
            isDarkMode
              ? "from-green-700 to-blue-700" // Versión oscura del gradiente
              : "from-green-500 to-blue-500" // Versión normal
          }`}
        >
          <h2 className="text-3xl font-bold text-center">Editar Evaluado</h2>
        </div>
        <form
          onSubmit={handleSubmit(async (data) => {
            // Crear una instancia de FormData para enviar la imagen y los datos juntos
            const formData = new FormData();

            // Agregar los datos del evaluado al FormData
            Object.keys(data).forEach((key) => {
              formData.append(key, data[key]);
            });

            // Verificar si hay una imagen cargada
            if (data.foto) {
              formData.append("foto", data.foto[0]); // Asegurarse de que el archivo esté incluido
            }

            // Ejecutar la mutación para enviar los datos y la imagen al backend
            try {
              await mutateEvaluado.mutateAsync({
                data: formData,
                tipo: "patch",
              });

              // Redirigir a la lista de evaluados solo después de que se guarden los datos
              navigate(`/evaluadosList?tipo=${tipoEvaluado}`);
            } catch (error) {
              console.error("Error al guardar los datos del evaluado:", error);
              // Aquí puedes mostrar algún mensaje de error en la UI si es necesario
            }
          })}
          className="space-y-6"
        >
          {/* Foto */}
          <div className="space-y-4">
            {/* Imagen Previa y Botón de Abrir Cámara */}
            <div className="flex items-center space-x-6">
              {/* Cuadro donde se mostrará la foto tomada o imagen cargada */}
              <div className="w-48 h-48 border border-gray-300 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center">
                {photoUrl ? (
                  <img
                    id="photo-preview"
                    src={photoUrl}
                    alt="Previsualización de la Foto"
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <p className="text-gray-500">No hay foto disponible</p>
                )}
              </div>

              {/* Botón para abrir la cámara con ícono */}
              <div>
                <Controller
                  name="foto"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <CameraInput
                      onCapture={(file) => {
                        field.onChange([file]);
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          const imgElement = document.getElementById(
                            "photo-preview"
                          ) as HTMLImageElement;
                          if (imgElement && e.target?.result) {
                            imgElement.src = e.target.result as string;
                          }
                        };
                        reader.readAsDataURL(file);
                      }}
                      isDarkMode={isDarkMode}
                      evaluadoUuid={uuid!}
                      nombreCompleto={`${queryEvaluado.data?.nombre || ""} ${
                        queryEvaluado.data?.apellido_paterno || ""
                      } ${queryEvaluado.data?.apellido_materno || ""}`}
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Seleccionar archivo
              </label>
              <Controller
                name="foto"
                control={control}
                render={({ field }) => (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Validar que sea una imagen
                        if (!file.type.startsWith("image/")) {
                          alert(
                            "Por favor, selecciona un archivo de imagen válido (JPEG, PNG, etc.)"
                          );
                          return;
                        }

                        field.onChange([file]);

                        // Mostrar previsualización
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          const imgElement = document.getElementById(
                            "photo-preview"
                          ) as HTMLImageElement;
                          if (imgElement && e.target?.result) {
                            imgElement.src = e.target.result as string;
                            imgElement.alt =
                              "Previsualización de la imagen seleccionada";
                            const messageElement =
                              imgElement.parentElement?.querySelector("p");
                            if (messageElement) {
                              messageElement.style.display = "none";
                            }
                          }
                        };
                        reader.readAsDataURL(file);

                        // Enviar al backend
                        try {
                          const nombreCompleto = `${
                            queryEvaluado.data?.nombre || ""
                          }_${queryEvaluado.data?.apellido_paterno || ""}_${
                            queryEvaluado.data?.apellido_materno || ""
                          }`.replace(/\s/g, "_");

                          const success = await sendPhotoToBackend(
                            file,
                            uuid!,
                            nombreCompleto
                          );

                          if (success) {
                            console.log(
                              "Imagen subida exitosamente desde archivo"
                            );
                          } else {
                            console.error(
                              "Error al subir la imagen desde archivo"
                            );
                          }
                        } catch (error) {
                          console.error(
                            "Error al enviar la imagen al backend:",
                            error
                          );
                        }
                      }
                    }}
                    className="mt-2 w-full p-2 border border-gray-300 rounded-md"
                  />
                )}
              />
            </div>
          </div>

          {/* Fecha de Evaluación */}
          <div className="w-full md:w-[265px]">
            <Label htmlFor="fecha_evaluacione" value="Fecha de Evaluación" />
            <Controller
              name="fecha_evaluacione"
              control={control}
              defaultValue={null}
              render={({ field, fieldState }) => (
                <TextInput
                  id="fecha_evaluacione"
                  type="date"
                  {...field}
                  color={fieldState.error ? "failure" : "gray"}
                  helperText={fieldState.error?.message}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Nombre */}
            <div className="space-y-2">
              <Label htmlFor="nombre" value="Nombre" />
              <Controller
                name="nombre"
                control={control}
                defaultValue=""
                rules={{ required: "Se requiere un nombre" }}
                render={({ field }) => (
                  <TextInput
                    id="nombre"
                    placeholder="Nombre..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                  />
                )}
              />
            </div>

            {/* Apellido Paterno */}
            <div className="space-y-2">
              <Label htmlFor="apellido_paterno" value="Apellido Paterno" />
              <Controller
                name="apellido_paterno"
                control={control}
                defaultValue=""
                rules={{ required: "Se requiere un apellido paterno" }}
                render={({ field }) => (
                  <TextInput
                    id="apellido_paterno"
                    placeholder="Apellido Paterno..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Apellido Materno */}
            <div className="space-y-2">
              <Label htmlFor="apellido_materno" value="Apellido Materno" />
              <Controller
                name="apellido_materno"
                control={control}
                defaultValue=""
                rules={{ required: "Se requiere un apellido materno" }}
                render={({ field }) => (
                  <TextInput
                    id="apellido_materno"
                    placeholder="Apellido Materno..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Correo */}
            <div className="space-y-2">
              <Label htmlFor="correo" value="Correo" />
              <Controller
                name="correo"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="correo"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatLowerCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                  />
                )}
              />
            </div>

            {/* Teléfono */}
            <div className="space-y-2">
              <Label htmlFor="telefono" value="Teléfono" />
              <Controller
                name="telefono"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="telefono"
                    placeholder="5512345678"
                    {...field}
                  />
                )}
              />
            </div>

            {/* Teléfono de recados */}
            <div className="space-y-2">
              <Label htmlFor="telefono_recados" value="Teléfono de recados" />
              <Controller
                name="telefono_recados"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Textarea
                    id="telefono_recados"
                    placeholder="5512345678"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    rows={5}
                  />
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Empresa */}
            <div className="space-y-2">
              <Label htmlFor="empresa_id" value="Empresa" />
              <Controller
                name="empresa_id"
                control={control}
                defaultValue=""
                rules={{ required: "Se requiere una empresa" }}
                render={({ field }) => (
                  <Select id="empresa_id" {...field}>
                    <option value="">Elige una Empresa</option>
                    {queryEmpresas.data?.map((empresa) => (
                      <option key={empresa.id} value={empresa.id}>
                        {empresa.nombre}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </div>

            {/* Empresa en la que labora */}
            <div className="space-y-2">
              <Label
                htmlFor="empresa_labora"
                value="Empresa en la que labora"
              />
              <Controller
                name="empresa_labora"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="empresa_labora"
                    placeholder="Empresa en la que labora..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* ¿Se Postula o Evalúa? */}
            <div className="space-y-2">
              <Label htmlFor="sepostula_evalua" value="¿Se Postula o Evalúa?" />
              <Controller
                name="sepostula_evalua"
                control={control}
                defaultValue={queryEvaluado.data?.sepostula_evalua}
                render={({ field }) => (
                  <Select id="sepostula_evalua" {...field}>
                    <option value="Se postula">Se postula</option>
                    <option value="Se evalúa">Se evalúa</option>
                  </Select>
                )}
              />
            </div>

            {/* Motivo de Evaluación */}
            <div className="space-y-2">
              <Label htmlFor="motivo_evaluacion" value="Motivo de Evaluación" />
              <Controller
                name="motivo_evaluacion"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <TextInput
                    id="motivo_evaluacion"
                    placeholder="Motivo de evaluación..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </div>

            {/* Tipo de Evaluación */}
            <div className="space-y-2">
              <Label htmlFor="tipo_evaluacion" value="Tipo de Evaluación" />
              <Controller
                name="tipo_evaluacion"
                control={control}
                defaultValue=""
                render={({ field, fieldState }) => (
                  <Select
                    id="tipo_evaluacion"
                    {...field}
                    color={fieldState.error ? "failure" : "gray"}
                    helperText={fieldState.error?.message}
                  >
                    <option value="">Selecciona una opción</option>
                    <option value="Nuevo Ingreso">Nuevo Ingreso</option>
                    <option value="Permanencia">Permanencia</option>
                    <option value="Ascenso">Ascenso</option>
                    <option value="Otro">Otro</option>
                  </Select>
                )}
              />
            </div>

            {/* Puesto */}
            <div className="space-y-2">
              <Label htmlFor="puesto" value="Puesto" />
              <Controller
                name="puesto"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="puesto"
                    placeholder="Puesto..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Fecha de ingreso */}
            <div className="space-y-2">
              <Label htmlFor="fecha_ingreso" value="Fecha de Ingreso" />
              <Controller
                name="fecha_ingreso"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput id="fecha_ingreso" type="date" {...field} />
                )}
              />
            </div>

            {/* Antigüedad */}
            <div className="space-y-2">
              <Label htmlFor="antiguedad" value="Antigüedad" />
              <Controller
                name="antiguedad"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="antiguedad"
                    type="number"
                    placeholder="Años de antigüedad"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Fecha de Nacimiento */}
            <div className="space-y-2">
              <Label htmlFor="fecha_nacimiento" value="Fecha de Nacimiento" />
              <Controller
                name="fecha_nacimiento"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput id="fecha_nacimiento" type="date" {...field} />
                )}
              />
            </div>

            {/* Género */}
            <div className="space-y-2">
              <Label htmlFor="genero" value="Género" />
              <Controller
                name="genero"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select id="genero" {...field}>
                    <option value="">Elige un género</option>
                    <option value="M">Masculino</option>
                    <option value="F">Femenino</option>
                    <option value="O">Otro</option>
                  </Select>
                )}
              />
            </div>

            {/* Lugar de Nacimiento */}
            <div className="space-y-2">
              <Label htmlFor="lugar_nacimiento" value="Lugar de Nacimiento" />
              <Controller
                name="lugar_nacimiento"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="lugar_nacimiento"
                    placeholder="Lugar de Nacimiento..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Nacionalidad */}
            <div className="space-y-2">
              <Label htmlFor="nacionalidad" value="Nacionalidad" />
              <Controller
                name="nacionalidad"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="nacionalidad"
                    placeholder="Nacionalidad..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* CURP */}
            <div className="space-y-2">
              <Label htmlFor="curp" value="CURP" />
              <Controller
                name="curp"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="curp"
                    placeholder="CURP..."
                    {...field}
                    maxLength={18}
                    onChange={(e) => {
                      field.onChange(formatUpperCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* RFC */}
            <div className="space-y-2">
              <Label htmlFor="rfc" value="RFC" />
              <Controller
                name="rfc"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="rfc"
                    placeholder="RFC..."
                    {...field}
                    maxLength={13}
                    onChange={(e) => {
                      field.onChange(formatUpperCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Escolaridad */}
            <div className="space-y-2">
              <Label htmlFor="escolaridad" value="Escolaridad" />
              <Controller
                name="escolaridad"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="escolaridad"
                    placeholder="Escolaridad..."
                    {...field}
                  />
                )}
              />
            </div>

            {/* Calle */}
            <div className="space-y-2">
              <Label htmlFor="calle" value="Calle" />
              <Controller
                name="calle"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="calle"
                    placeholder="Calle..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Número Exterior */}
            <div className="space-y-2">
              <Label htmlFor="numero_exterior" value="Número Exterior" />
              <Controller
                name="numero_exterior"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="numero_exterior"
                    placeholder="Número Exterior..."
                    {...field}
                  />
                )}
              />
            </div>

            {/* Número Interior */}
            <div className="space-y-2">
              <Label htmlFor="numero_interior" value="Número Interior" />
              <Controller
                name="numero_interior"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="numero_interior"
                    placeholder="Número Interior..."
                    {...field}
                  />
                )}
              />
            </div>

            {/* Referencia */}
            <div className="space-y-2">
              <Label htmlFor="referencia" value="Referencia" />
              <Controller
                name="referencia"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="referencia"
                    placeholder="Referencia"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Colonia */}
            <div className="space-y-2">
              <Label htmlFor="colonia" value="Colonia" />
              <Controller
                name="colonia"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="colonia"
                    placeholder="Colonia..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Código Postal */}
            <div className="space-y-2">
              <Label htmlFor="codigo_postal" value="Código Postal" />
              <Controller
                name="codigo_postal"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="codigo_postal"
                    placeholder="Código Postal..."
                    {...field}
                  />
                )}
              />
            </div>

            {/* Municipio */}
            <div className="space-y-2">
              <Label htmlFor="municipio" value="Municipio" />
              <Controller
                name="municipio"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="municipio"
                    placeholder="Municipio..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Estado */}
            <div className="space-y-2">
              <Label htmlFor="estado" value="Estado" />
              <Controller
                name="estado"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="estado"
                    placeholder="Estado..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* País */}
            <div className="space-y-2">
              <Label htmlFor="pais" value="País" />
              <Controller
                name="pais"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="pais"
                    placeholder="País..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* Años Viviendo en el Domicilio */}
            <div className="space-y-2">
              <Label
                htmlFor="anios_domicilio_viviendo"
                value="Años Viviendo en ese Domicilio"
              />
              <Controller
                name="anios_domicilio_viviendo"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Textarea
                    id="anios_domicilio_viviendo"
                    placeholder="Años Viviendo..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    rows={5}
                  />
                )}
              />
            </div>

            {/* Personas en la Vivienda */}
            <div className="space-y-2">
              <Label
                htmlFor="personas_vivienda"
                value="Personas Viviendo en el Domicilio"
              />
              <Controller
                name="personas_vivienda"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="personas_vivienda"
                    placeholder="Personas en el Domicilio..."
                    {...field}
                    onChange={(e) => handleNumberChange(e, field)}
                    min="0"
                  />
                )}
              />
            </div>

            {/* Estado Civil */}
            <div className="space-y-2">
              <Label htmlFor="estado_civil" value="Estado Civil" />
              <Controller
                name="estado_civil"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select id="estado_civil" {...field}>
                    <option value="">Selecciona un Estado Civil</option>
                    <option value="soltero">Soltero</option>
                    <option value="casado">Casado</option>
                    <option value="concubinato">Concubinato</option>
                    <option value="divorciado">Divorciado</option>
                    <option value="viudo">Viudo</option>
                  </Select>
                )}
              />
            </div>

            {/* Con quien vive */}
            <div className="space-y-2">
              <Label htmlFor="vive_con" value="Con quien vive" />
              <Controller
                name="vive_con"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="vive_con"
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                    placeholder="Escribe con quien vives"
                  />
                )}
              />
            </div>

            {/* TIEMPO DE CASADO(A) Y/O CONCUBINATO */}
            <div className="space-y-2">
              <Label
                htmlFor="tiempo_casado"
                value="Tiempo de casado(a) y/o concubinato"
              />
              <Controller
                name="tiempo_casado"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="tiempo_casado"
                    placeholder="Tiempo de casado(a)..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* A QUE SE DEDICA SU ESPOSA(O) Y/O CONCUBINO */}
            <div className="space-y-2">
              <Label
                htmlFor="conyuge_oficio"
                value="A que se dedica su esposo(a) y/o concubino"
              />
              <Controller
                name="conyuge_oficio"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="conyuge_oficio"
                    placeholder="A que se dedica su esposo(a)..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* NOMBRE DE ESPOSA(O) Y/O CONCUBINO */}
            <div className="space-y-2">
              <Label
                htmlFor="nombre_conyuge"
                value="Nombre de esposa(o) y/o concubino"
              />
              <Controller
                name="nombre_conyuge"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    id="nombre_conyuge"
                    placeholder="Nombre..."
                    {...field}
                    onChange={(e) => {
                      field.onChange(formatTitleCase(e.target.value));
                    }}
                  />
                )}
              />
            </div>

            {/* CUANTOS HIJOS TIENE */}
            <div className="space-y-2">
              <Label htmlFor="cuantos_hijos" value="Cuantos hijos tiene" />
              <Controller
                name="cuantos_hijos"
                control={control}
                defaultValue={0}
                rules={{
                  min: {
                    value: 0,
                    message: "Mínimo 0 hijos",
                  },
                  max: {
                    value: 4,
                    message: "Máximo 4 hijos permitidos",
                  },
                }}
                render={({ field, fieldState }) => {
                  // Asegura que el valor sea un número (incluso si viene como string)
                  const value =
                    typeof field.value === "string"
                      ? parseInt(field.value, 10) || 0
                      : field.value || 0;

                  return (
                    <TextInput
                      id="cuantos_hijos"
                      type="number"
                      placeholder="Hijos..."
                      {...field}
                      value={value}
                      min={0}
                      max={4}
                      onChange={(e) => {
                        const numValue = parseInt(e.target.value, 10) || 0;
                        const clampedValue = Math.min(Math.max(numValue, 0), 4);
                        field.onChange(clampedValue);
                        setNumeroHijos(clampedValue);
                      }}
                      color={fieldState.error ? "failure" : "gray"}
                      helperText={fieldState.error?.message}
                    />
                  );
                }}
              />
            </div>

            {Array.from({ length: numeroHijos }, (_, index) => {
              const nombreKey = `nombre_hijo${
                index + 1
              }` as keyof EvaluadoInterface;
              const edadKey = `edad_hijo${
                index + 1
              }` as keyof EvaluadoInterface;

              return (
                <div key={index} className="space-y-2">
                  {/* NOMBRE HIJO */}
                  <Label
                    htmlFor={`nombreh${index + 1}`}
                    value={`Nombre del Hijo ${index + 1}`}
                  />
                  <Controller
                    name={`nombreh${index + 1}`}
                    control={control}
                    defaultValue={queryEvaluado.data?.[nombreKey] || ""}
                    render={({ field, fieldState }) => (
                      <TextInput
                        id={`nombreh${index + 1}`}
                        placeholder="Nombre..."
                        {...field}
                        onChange={(e) => {
                          field.onChange(formatTitleCase(e.target.value));
                        }}
                        color={fieldState.error ? "failure" : "gray"}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />

                  {/* EDAD HIJO */}
                  <Label
                    htmlFor={`edadh${index + 1}`}
                    value={`Edad del Hijo ${index + 1}`}
                  />
                  <Controller
                    name={`edadh${index + 1}`}
                    control={control}
                    defaultValue={queryEvaluado.data?.[edadKey] || ""}
                    render={({ field, fieldState }) => (
                      <TextInput
                        id={`edadh${index + 1}`}
                        type="number"
                        placeholder="Edad..."
                        {...field}
                        color={fieldState.error ? "failure" : "gray"}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                </div>
              );
            })}
          </div>

          <div className="flex justify-center pt-4 space-x-4">
            <Button
              type="button"
              className={`w-1/2 ${
                isDarkMode ? "bg-red-700" : "bg-red-600"
              } text-white`}
              onClick={() => {
                handleCancel();
                window.scrollTo(0, 0);
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className={`w-1/2 ${
                isDarkMode ? "bg-green-700" : "bg-green-600"
              } text-white`}
            >
              Guardar
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
